import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { issuesOf, newKey, useLedgerError, type WorkflowAction } from './ledger-ui.js';
import type { FuelSource } from './source.js';
import type {
  FuelContextData,
  FuelDocumentKind,
  FuelRecordDetail,
  FuelRecordKind,
  FuelRecordSummary,
} from './types.js';

/**
 * Alım ve hareket ekranlarının ortak durumu (ADR 0117 Ek Y2): bağlam (tanklar, yakıtlar,
 * gemidekiler), kayıt listesi, seçili kaydın ayrıntısı ve onay akışı. Yeni kayıt istemcinin
 * idempotency anahtarıyla açılır (ADR 0117 §5): ağ hatasında tekrar gönderim aynı kaydı döner.
 */
export function useLedgerRecords(
  source: FuelSource,
  vesselId: string | null,
  kind: FuelRecordKind,
  onVessel?: (data: FuelContextData) => void,
) {
  const { t } = useTranslation();
  const errorText = useLedgerError();
  const [context, setContext] = useState<FuelContextData | null>(null);
  const [records, setRecords] = useState<FuelRecordSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<FuelRecordDetail | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [loadCode, setLoadCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldIssues, setFieldIssues] = useState<Record<string, string>>({});
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState<WorkflowAction | null>(null);
  const [key, setKey] = useState(newKey);
  const [pendingFiles, setPendingFiles] = useState<Partial<Record<FuelDocumentKind, File>>>({});
  const [removed, setRemoved] = useState<string[]>([]);

  const loadDetail = useCallback(
    async (id: string | null) => {
      setSelectedId(id);
      setPendingFiles({});
      setRemoved([]);
      setFieldIssues({});
      if (id === null) {
        setDetail(null);
        setKey(newKey());
        return;
      }
      setDetail(await source.record(vesselId, kind, id));
    },
    [source, vesselId, kind],
  );

  const load = useCallback(
    async (select?: string | null) => {
      try {
        const [ctx, list] = await Promise.all([
          source.context(vesselId),
          source.records(vesselId, kind),
        ]);
        setContext(ctx);
        setRecords(list.items);
        onVessel?.(ctx);
        // Seçim verilmezse en yeni kayıt; kayıt yoksa yeni form
        await loadDetail(select !== undefined ? select : (list.items[0]?.id ?? null));
        setState('ready');
      } catch (failure) {
        setLoadCode((failure as { code?: string }).code ?? 'GENERIC');
        setState('error');
      }
    },
    [source, vesselId, kind, loadDetail, onVessel],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const people = useMemo(
    () => new Map((context?.crew ?? []).map((p) => [p.id, p.name])),
    [context],
  );

  const editor = source.ledgerEditor;

  /** Kaydı yazar (yeni ya da taslak) ve bekleyen dosyaları ekler; kaydın kimliği döner. */
  const persist = useCallback(
    async (input: Record<string, unknown>, extra: { tankId?: string } = {}) => {
      if (!editor) throw Object.assign(new Error('FORBIDDEN'), { code: 'FORBIDDEN' });
      let id = selectedId;
      if (id === null) {
        const saved = await editor.save(kind, { idempotencyKey: key, input, ...extra });
        id = saved.id;
      } else if (detail && (detail.status === 'draft' || detail.status === 'returned')) {
        await editor.save(kind, { id, rowVersion: detail.rowVersion, input });
      }
      for (const attachmentId of removed) await editor.detach(kind, id, attachmentId);
      for (const [document, file] of Object.entries(pendingFiles) as [FuelDocumentKind, File][]) {
        const stored = await editor.upload(file);
        await editor.attach(kind, id, { sha256: stored.sha256, fileName: file.name, document });
      }
      // Yazılan kayıt hemen seçilir: sonraki adım (gönderim) düşse de bir sonraki deneme yeni
      // kayıt açmaz ve güncel `row_version` ile düzeltir
      const fresh = await source.record(vesselId, kind, id);
      setSelectedId(id);
      setDetail(fresh);
      setPendingFiles({});
      setRemoved([]);
      return id;
    },
    [editor, selectedId, kind, key, detail, removed, pendingFiles, source, vesselId],
  );

  const run = useCallback(
    async (action: WorkflowAction, work: () => Promise<string | null>, message: string) => {
      setBusy(action);
      setError(null);
      setDone(null);
      setFieldIssues({});
      try {
        const id = await work();
        await load(id);
        setDone(message);
      } catch (failure) {
        setError(errorText(failure));
        setFieldIssues(issuesOf(failure));
      } finally {
        setBusy(null);
      }
    },
    [load, errorText],
  );

  const save = (input: Record<string, unknown>, extra?: { tankId?: string }) =>
    run('save', () => persist(input, extra), t('fuel.records.saved'));

  const submit = (input: Record<string, unknown>, extra?: { tankId?: string }) =>
    run(
      'submit',
      async () => {
        const id = await persist(input, extra);
        const fresh = await source.record(vesselId, kind, id);
        await editor?.submit(kind, id, fresh.approval.contentHash);
        return id;
      },
      t('fuel.records.submitted'),
    );

  const correct = () =>
    run(
      'correct',
      async () => {
        if (!editor || !detail) return null;
        const opened = await editor.correct(kind, detail.id, newKey());
        return opened.id;
      },
      t('fuel.records.correctionOpened', { no: detail?.recordNo ?? '' }),
    );

  /** Onay, iade ve ters kayıt (pencereden). */
  const decide = async (
    mode: 'approve' | 'return' | 'reverse',
    input: { reason: string; password?: string },
  ) => {
    if (!editor || !detail) return;
    if (mode === 'reverse') {
      await editor.reverse(kind, detail.id, detail.rowVersion, input.reason);
      await load(detail.id);
      setDone(t('fuel.records.reversed'));
      return;
    }
    const pending = detail.approval.pending;
    if (!pending) return;
    await editor.decide(kind, detail.id, {
      decision: mode === 'approve' ? 'approved' : 'returned',
      reason: mode === 'return' ? input.reason : null,
      contentHash: detail.approval.contentHash,
      expected: { submissionId: pending.submissionId, step: pending.step },
      ...(input.password ? { password: input.password } : {}),
    });
    await load(detail.id);
    setDone(mode === 'approve' ? t('fuel.records.approved') : t('fuel.records.returned'));
  };

  return {
    context,
    records,
    selectedId,
    detail,
    state,
    loadCode,
    error,
    fieldIssues,
    done,
    busy,
    people,
    pendingFiles,
    setPendingFiles,
    removed,
    setRemoved,
    select: (id: string | null) => {
      setError(null);
      setDone(null);
      void loadDetail(id);
    },
    reload: load,
    save,
    submit,
    correct,
    decide,
  };
}
